import React, { useState, useEffect, useMemo, Dispatch, SetStateAction } from 'react';
import {
  OrganizationService,
  SendWhatsAppMessageNodeData,
  UpdateWorkflowNodeRequest,
  WorkflowNodeType,
  WorkflowsService,
  WhatsAppService,
} from '@/api';
import { PhoneNumberDto } from '@/api/models/PhoneNumberDto';
import { WhatsAppTemplateMessageInfoDto } from '@/api/models/WhatsAppTemplateMessageInfoDto';
import { BusinessAccount } from '@/api/models/BusinessAccount';
import { useStore } from '@nanostores/react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNodes } from '@xyflow/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { TestNodeDrawer } from './TestNodeDrawer';
import { toast } from 'sonner';
import { FormattedNode } from '../../WorkflowBuilder';
import {
  useAutoSaveDebouncedCallback,
  useNodeConfigurationEventListener,
} from '../utils/nodeConfigurationUtils';
import { Loader2 } from 'lucide-react';

export default function SendWhatsAppConfiguration({
  selectedNode,
  setNodes,
  readOnly = false,
  setAllRequiredFieldsOfNodeConfigurationFilled,
}: {
  setNodes: any;
  selectedNode: FormattedNode;
  readOnly?: boolean;
  setAllRequiredFieldsOfNodeConfigurationFilled: Dispatch<SetStateAction<boolean>>;
}) {
  const wF = useStore($workflow);
  const org = useStore($org);
  const nodes = useNodes();
  const queryClient = useQueryClient();

  console.log('org', org);

  const {
    data: orgPhoneNumbers,
    error: orgPhoneError,
    isLoading: loadingOrgPhoneNumbers,
    isFetching: fetchingOrgPhoneNumbers,
    refetch,
  } = useQuery({
    queryKey: ['orgPhoneNumbers', org?.id],
    enabled: !!org?.id,
    queryFn: async () => {
      if (!org?.id) return [];
      const res = await OrganizationService.getOrganizationPhoneNumbers(
        org?.id || '',
      );
      return res;
    },
    refetchOnMount: true,
    refetchInterval: false,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (orgPhoneError) toast.error('Failed to load phone numbers');
  }, [orgPhoneError]);

  useEffect(() => {
    if (wF) {
      if (wF.type === 'BulkRun') {
        setToNumber('{UserPhoneNumber}');
      }
    }
    if (selectedNode) {
      if (isSendWhatsAppNodeData(selectedNode.data)) {
        setToNumber(selectedNode.data.to ?? '');
      }
    }
  }, [selectedNode]);

  // ----------------------------------------------------------------------------
  // 0) Local form state — we'll hydrate all five fields below, *before* any queries
  // ----------------------------------------------------------------------------
  const [selectedBiz, setSelectedBiz]           = useState<string>('');
  const [selectedPhone, setSelectedPhone]       = useState<string>('');
  const [toNumber, setToNumber]                 = useState<string>('');
  const [templateName, setTemplateName]         = useState<string>('');
  const [parametersInput, setParametersInput]   = useState<string>('');
  const [testDrawerOpen, setTestDrawerOpen]     = useState(false);

  // helper type guard
  function isSendWhatsAppNodeData(data: any): data is SendWhatsAppMessageNodeData {
    return data.type === 'SEND_WHATSAPP_MESSAGE';
  }

  // ----------------------------------------------------------------------------
  // 1) When the drawer opens (i.e. when selectedNode changes), hydrate ALL state
  // ----------------------------------------------------------------------------
  useEffect(() => {
    if (!selectedNode || !isSendWhatsAppNodeData(selectedNode.data)) return;
    const d = selectedNode.data;
    // order doesn't matter here, but we must do it *before* any queries run
    setSelectedBiz(d.business_id || '');
    setSelectedPhone(d.phone_number_id);
    setToNumber(d.to);
    setTemplateName(d.template_name);
    setParametersInput((d.parameters ?? []).join(', '));
  }, [selectedNode]);

  // ----------------------------------------------------------------------------
  // 2) Load business accounts (always available)
  // ----------------------------------------------------------------------------
  const { data: businesses = [], isLoading: loadingBiz, error: bizError } = useQuery<BusinessAccount[]>({
    queryKey: ['wa-businesses'],
    queryFn: () => WhatsAppService.getWhatsAppBusinessAccounts(org?.id || ''),
  });
  useEffect(() => { if (bizError) toast.error('Failed to load WhatsApp businesses'); }, [bizError]);

  useEffect(() => {
    if (businesses.length === 1) {
      setSelectedBiz(businesses[0].id || '');
    }
  }, [businesses, setSelectedBiz]);

  // ----------------------------------------------------------------------------
  // 3) Sync phone numbers for that business
  // ----------------------------------------------------------------------------
  const syncNumbers = useMutation({
    mutationFn: (biz: string) => WhatsAppService.whatsAppStorePhoneNumbers(biz, org?.id || ''),
    onSuccess: () => {
      toast.success('Phone numbers synced!');
      // after sync, refetch stored numbers (next effect)
      queryClient.invalidateQueries({ queryKey: ['wa-phone-numbers', selectedBiz] });
    },
    onError: () => toast.error('Failed to sync phone numbers'),
  });

  // ----------------------------------------------------------------------------
  // 4) Load stored phone numbers *only once* we have a business_id
  // ----------------------------------------------------------------------------
  const { data: phoneNumbers = [], isLoading: loadingNumbers, error: phoneError } = useQuery<PhoneNumberDto[]>({
    queryKey: ['wa-phone-numbers', selectedBiz],
    queryFn: () => WhatsAppService.getWhatsAppPhoneNumbers(org?.id || ''),
    enabled: !!selectedBiz,
  });
  useEffect(() => { if (phoneError) toast.error('Failed to load phone numbers'); }, [phoneError]);

  // ----------------------------------------------------------------------------
  // 5) Load templates *only once* we have biz + at least one from-number
  // ----------------------------------------------------------------------------
  const { data: templates = [], isLoading: loadingTemplates, error: templateError } = useQuery<WhatsAppTemplateMessageInfoDto[]>({
    queryKey: ['wa-templates', selectedBiz],
    queryFn: () => WhatsAppService.getWhatsAppTemplates(org?.id || '', selectedBiz),
    enabled: !!selectedBiz && phoneNumbers.length > 0,
  });
  useEffect(() => { if (templateError) toast.error('Failed to load templates'); }, [templateError]);

  // ----------------------------------------------------------------------------
  // 6) Preview & validation
  // ----------------------------------------------------------------------------
  const selectedTpl = templates.find(t => t.name === templateName);
  const expectedParams = selectedTpl?.parameterCount ?? 0;
  const params = parametersInput.split(',').map(p => p.trim()).filter(Boolean);
  const paramsValid = params.length === expectedParams;

  const previewMessage = useMemo(() => {
    if (!selectedTpl) return 'Select a template…';
    if (params.length !== expectedParams)
      return `Template: ${selectedTpl.bodyTemplate}\nPlaceholders: ${expectedParams}`;
    let msg = selectedTpl.bodyTemplate || '';
    params.forEach((v, i) => {
      msg = msg.replace(new RegExp(`\\{\\{${i+1}\\}\\}`, 'g'), v);
    });
    return msg;
  }, [selectedTpl, parametersInput]);

  // ----------------------------------------------------------------------------
  // 7) Apply changes locally to the node (XYFlow)
  // ----------------------------------------------------------------------------
  function updateNodeLocally() {
    if (readOnly) return;
    const copy = JSON.parse(JSON.stringify(nodes));
    const idx = copy.findIndex((n: any) => n.id === selectedNode.id);
    if (idx < 0) return;

    // use the language code of the selected template instead of hardcoded 'en'
    const selectedTemplate = templates.find(t => t.name === templateName);
    const templateLanguageCode = selectedTemplate?.language || 'en';

    copy[idx].data = {
      type: 'SEND_WHATSAPP_MESSAGE' as WorkflowNodeType,
      business_id: selectedBiz,
      phone_number_id: selectedPhone,
      to: toNumber,
      template_name: templateName,
      parameters: params,
      language_code: templateLanguageCode,
    };
    setNodes(copy);
    return copy[idx] as FormattedNode;
  }

  // ----------------------------------------------------------------------------
  // 8) Persist (debounced + on confirm)
  // ----------------------------------------------------------------------------
  const saveMutation = useMutation({
    mutationFn: (payload: { nodeId: string; req: UpdateWorkflowNodeRequest }) =>
      WorkflowsService.updateWorkflowNode(wF!.id, payload.nodeId, payload.req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['getWorkflow', wF?.id] });
      toast.success('Configuration saved');
    },
    onError: () => toast.error('Save failed'),
  });

  async function saveConfigurations() {
    if (!selectedBiz)     return toast.error('Pick a business');
    if (!selectedPhone)   return toast.error('Pick a From number');
    if (!toNumber)        return toast.error('Enter a To number');
    if (!templateName)    return toast.error('Pick a template');
    if (!paramsValid)     return toast.error(`Enter exactly ${expectedParams} parameters`);
    setAllRequiredFieldsOfNodeConfigurationFilled(true);
    const updated = updateNodeLocally();
    if (updated) {
      await saveMutation.mutateAsync({ nodeId: updated.id, req: { data: updated.data } });
    }
  }

  useAutoSaveDebouncedCallback(saveConfigurations, 2000, [
    selectedBiz,
    selectedPhone,
    toNumber,
    templateName,
    parametersInput,
  ]);
  useNodeConfigurationEventListener('confirmSaveChanges', saveConfigurations);

  // ----------------------------------------------------------------------------
  // 9) UI
  // ----------------------------------------------------------------------------
  return (
    <div className="flex flex-col bg-gray-50 p-4 space-y-4">
      {/* BUSINESS & SYNC */}
      {!phoneNumbers.length && (
        <div>
          <label className="block text-sm font-medium text-gray-700">Business</label>
          {loadingBiz ? (
            <p>Loading…</p>
          ) : (
            <div className="flex space-x-2 mt-1">
              <select
                className="rounded border px-2 py-1"
                value={selectedBiz}
                onChange={e => setSelectedBiz(e.target.value)}
                disabled={syncNumbers.isPending}
              >
                <option value="">Select business</option>
                {businesses.map(b => (
                  <option key={b.id} value={b.id || ''}>
                    {b.name}
                  </option>
                ))}
              </select>
              <Button
                onClick={() => syncNumbers.mutate(selectedBiz)}
                disabled={!selectedBiz || syncNumbers.isPending}
              >
                {syncNumbers.isPending ? 'Syncing…' : 'Sync Numbers'}
              </Button>
            </div>
          )}
        </div>
      )}

      {/* FROM PHONE */}
      <div>
        <label className="block text-sm font-medium text-gray-700">From</label>
        {loadingNumbers ? (
          <p>Loading numbers…</p>
        ) : (
          <select
            className="mt-1 w-full rounded border px-2 py-1"
            value={selectedPhone}
            onChange={e => setSelectedPhone(e.target.value)}
            disabled={!selectedBiz || loadingNumbers || !phoneNumbers.length}
          >
            <option value="">Select number</option>
            {phoneNumbers.map(pn => (
              <option key={pn.id} value={pn.id!}>
                {pn.name} (+{pn.displayPhoneNumber})
              </option>
            ))}
          </select>
        )}
      </div>

      {/* TO NUMBER */}
      <div className="mt-4">
        <label
        htmlFor="to"
        className="block text-sm font-medium text-gray-700"
        >
        To
        </label>
        <Input
          id="to"
          name="to"
          disabled={readOnly || wF?.type === 'BulkRun'}
          value={toNumber} // local state
          onChange={e => {
            if (!readOnly) {
            setToNumber(e.target.value);
            }
        }}
        className={`mt-1 font-semibold text-blue-800 ${readOnly ? 'cursor-not-allowed bg-gray-200 text-gray-500' : ''}`}
        placeholder="Recipient's phone number"
        />
      </div>

      {/* TEMPLATE */}
      <div>
        <label className="block text-sm font-medium text-gray-700">Template</label>
        {loadingTemplates ? (
          <p>Loading templates…</p>
        ) : (
          <select
            className="mt-1 w-full rounded border px-2 py-1"
            value={templateName}
            onChange={e => setTemplateName(e.target.value)}
            disabled={!selectedBiz || loadingTemplates || !templates.length}
          >
            <option value="">Select template</option>
            {templates.map(t => (
              <option key={t.name} value={t.name}>
                {t.name} — placeholders: {t.parameterCount}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* PARAMETERS */}
      {expectedParams > 0 && (
        <div>
          <label className="block text-sm font-medium text-gray-700">
            Parameters ({expectedParams})
          </label>
          <Textarea
            className="mt-1"
            rows={3}
            placeholder={`Enter ${expectedParams} value(s), comma-separated`}
            value={parametersInput}
            onChange={e => setParametersInput(e.target.value)}
          />
        </div>
      )}

      {/* PREVIEW */}
      <div>
        <label className="block text-sm font-medium text-gray-700">Preview</label>
        <Textarea className="mt-1 bg-gray-100" rows={5} disabled value={previewMessage} />
      </div>

      <p className="text-sm text-gray-500">
        Variables: [company_name], [customer_name], [invoice_name], [phone_number],
        [email], [street_address], [city], [state], [zip_code], [country],
        [invoice_amount], [invoice_paid_amount], [invoice_balance],
        [invoice_due_date], [invoice_invoice_date], [payment_link],
        [company_phone_number]
      </p>

      {/* ACTIONS */}
      <div className="flex justify-end space-x-2">
        <Button onClick={() => setTestDrawerOpen(true)}>Test</Button>
        <Button
          onClick={saveConfigurations}
          disabled={
            !selectedBiz ||
            !selectedPhone ||
            !toNumber ||
            !templateName ||
            (expectedParams > 0 && !paramsValid) ||
            saveMutation.isPending
          }
        >
          Save
        </Button>
      </div>

      <TestNodeDrawer
        open={testDrawerOpen}
        onClose={() => setTestDrawerOpen(false)}
        node={selectedNode}
        workflow={wF}
      />
    </div>
  );
}
